import { SerializedError } from "@reduxjs/toolkit"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import Router from "next/router"
import { useCallback, useMemo, useState } from "react"
import { toast } from "shared-web/lib/toast"

import { useAppDispatch } from "app/hooks"
import { ApiError, getUserMessage, isApiError } from "features/api/apiSlice"
import {
  useAuthenticateOneTimePasswordMutation,
  useSendOneTimePasswordEmailMutation,
  useSendOneTimePasswordSMSMutation,
} from "features/api/auth"

import { setLastUsedSignInMethod, setLoggedInStaffCode } from "./authSlice"
import { useSignInContext } from "./SignInContext"
import { stytchErrorMessage } from "./stytchErrorMessage"
import { useGetCompanyForCurrentSubdomain } from "./useGetCompanyForCurrentSubdomain"

function useOTPSignIn({
  staffExternalId,
  onSend,
  onResend,
  sendError,
  methodId,
  onAuthenticate,
}: {
  staffExternalId: string
  onSend: (staffCode: string, companyId?: PBUUID) => Promise<void>
  onResend: (staffCode: string, companyId?: PBUUID) => Promise<void>
  sendError: ApiError | SerializedError | undefined
  methodId: string
  onAuthenticate: () => void
}) {
  const dispatch = useAppDispatch()

  const { currentData: company } = useGetCompanyForCurrentSubdomain()
  const companyId = company?.id

  const { redirectPath } = useSignInContext()

  const [
    authenticateOneTimePassword,
    { error: authenticateError, reset: resetAuthenticate },
  ] = useAuthenticateOneTimePasswordMutation()
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  const sendOTP = useCallback(
    async (staffCode: string) => {
      resetAuthenticate()
      await onSend(staffCode, companyId)
    },
    [resetAuthenticate, companyId, onSend]
  )

  const resendOTP = useCallback(async () => {
    resetAuthenticate()
    try {
      await onResend(staffExternalId, companyId)
      toast.success({ message: "New code sent" })
    } catch (err) {
      toast.error({
        message: "Couldn't send code. Please try again.",
        error: err,
      })
    }
  }, [resetAuthenticate, onResend, staffExternalId, companyId])

  const sendOTPError = useMemo(
    () => sendOTPErrorToMessage(sendError),
    [sendError]
  )

  const authenticateOTP = useCallback(
    async (code: string) => {
      try {
        setIsAuthenticating(true)

        await authenticateOneTimePassword({
          methodId,
          companyId,
          code,
        }).unwrap()

        dispatch(setLoggedInStaffCode(staffExternalId))

        onAuthenticate()

        if (redirectPath) {
          Router.push(redirectPath)
        }
      } catch {
        setIsAuthenticating(false)
      }
    },
    [
      methodId,
      companyId,
      authenticateOneTimePassword,
      dispatch,
      staffExternalId,
      onAuthenticate,
      redirectPath,
    ]
  )

  const authenticateOTPError = useMemo(
    () => authenticateOTPErrorToMessage(authenticateError),
    [authenticateError]
  )

  return {
    sendOTP,
    sendOTPError,
    authenticateOTP,
    isAuthenticating,
    authenticateOTPError,
    resendOTP,
  }
}

export function sendOTPErrorToMessage(
  err?: ApiError | SerializedError
): string | null {
  if (!err) return null
  if (isApiError(err) && err.meta && "stytch_error_type" in err.meta) {
    return stytchErrorMessage(err.meta.stytch_error_type as string)
  }
  if (isApiError(err) && err.code === "not_found") {
    return "ID not found. Please try again."
  }
  return (
    (isApiError(err) && getUserMessage(err)) ||
    "Something went wrong. Please try again."
  )
}

export function authenticateOTPErrorToMessage(
  err?: ApiError | SerializedError
): string | null {
  if (!err) return null
  if (isApiError(err) && err.meta && "stytch_error_type" in err.meta) {
    return stytchErrorMessage(err.meta.stytch_error_type as string)
  }
  return (
    (isApiError(err) && getUserMessage(err)) ||
    "Something went wrong. Please try again."
  )
}

export function useTextMessageSignIn() {
  const [sendTextMessage, { isLoading, data, originalArgs, error }] =
    useSendOneTimePasswordSMSMutation()

  const staffExternalId = originalArgs?.staffCode ?? ""
  const methodId = data?.phoneId ?? ""
  const phoneLast4 = data?.phoneLast4 ?? ""

  const [prevPhoneLast4, setPrevPhoneLast4] = useState(phoneLast4)
  if (phoneLast4 && prevPhoneLast4 !== phoneLast4) {
    setPrevPhoneLast4(phoneLast4)
  }
  const { setStep } = useSignInContext()

  const onSend = useCallback(
    async (staffCode: string, companyId?: PBUUID) => {
      try {
        await sendTextMessage({ staffCode, companyId }).unwrap()
        setStep("text-message-code")
      } catch {}
    },
    [sendTextMessage, setStep]
  )

  const onResend = useCallback(
    async (staffCode: string, companyId?: PBUUID) => {
      await sendTextMessage({ staffCode, companyId }).unwrap()
    },
    [sendTextMessage]
  )

  const dispatch = useAppDispatch()

  const onAuthenticate = useCallback(() => {
    dispatch(setLastUsedSignInMethod("text-message"))
  }, [dispatch])

  const {
    sendOTP,
    sendOTPError,
    authenticateOTP,
    isAuthenticating,
    authenticateOTPError,
    resendOTP,
  } = useOTPSignIn({
    staffExternalId,
    onSend,
    onResend,
    sendError: error,
    methodId,
    onAuthenticate,
  })

  return {
    sendTextMessage: sendOTP,
    isSendingTextMessage: isLoading,
    sendTextMessageError: sendOTPError,
    authenticateTextMessage: authenticateOTP,
    isAuthenticatingTextMessage: isAuthenticating,
    authenticateTextMessageError: authenticateOTPError,
    resendTextMessage: resendOTP,
    maskedPhoneNumber: `+1 XXX-XXX-${phoneLast4 || prevPhoneLast4}`,
  }
}

export function useEmailSignIn() {
  const [sendEmail, { isLoading, data, originalArgs, error }] =
    useSendOneTimePasswordEmailMutation()

  const staffExternalId = originalArgs?.staffCode ?? ""
  const methodId = data?.emailId ?? ""
  const maskedEmail = data?.maskedEmailAddress ?? ""

  const [prevMaskedEmail, setPrevMaskedEmail] = useState(maskedEmail)
  if (maskedEmail && prevMaskedEmail !== maskedEmail) {
    setPrevMaskedEmail(maskedEmail)
  }

  const { setStep } = useSignInContext()

  const onSend = useCallback(
    async (staffCode: string, companyId?: PBUUID) => {
      try {
        await sendEmail({ staffCode, companyId }).unwrap()
        setStep("email-code")
      } catch {}
    },
    [sendEmail, setStep]
  )

  const onResend = useCallback(
    async (staffCode: string, companyId?: PBUUID) => {
      await sendEmail({ staffCode, companyId }).unwrap()
    },
    [sendEmail]
  )

  const dispatch = useAppDispatch()

  const onAuthenticate = useCallback(() => {
    dispatch(setLastUsedSignInMethod("email"))
  }, [dispatch])

  const {
    sendOTP,
    sendOTPError,
    authenticateOTP,
    isAuthenticating,
    authenticateOTPError,
    resendOTP,
  } = useOTPSignIn({
    staffExternalId,
    onSend,
    onResend,
    sendError: error,
    methodId,
    onAuthenticate,
  })

  return {
    sendEmail: sendOTP,
    isSendingEmail: isLoading,
    sendEmailError: sendOTPError,
    authenticateEmail: authenticateOTP,
    isAuthenticatingEmail: isAuthenticating,
    authenticateEmailError: authenticateOTPError,
    resendEmail: resendOTP,
    maskedEmailAddress: maskedEmail || prevMaskedEmail,
  }
}
